/* eslint-disable complexity */
/* eslint-disable max-statements */
import { useCallback, useState, useEffect } from 'react';
import { getFreshchatData, updateFreshchatData, shouldFreshChatClosed } from '../utils';
import { updateFreshchatRestoreId } from '../helpers';
import { useBerryHelpers } from '@mindtickle/ui-shell-core/Berry';
import useFreshchatHelpers from './useFreshchatHelpers';
import { isNull } from '~/utils';

const useFreshchatHandlers = ({ auth, isBerryChatDisabled }: any) => {
  const userId = auth?.user.id;
  const companyId = auth?.company.id;

  const [initializedState, setInitializedState] = useState<{
    freshchatInitialized: Boolean | null;
    berryInitialized: Boolean | null;
  }>({
    freshchatInitialized: null,
    berryInitialized: null,
  });

  const updateInitializedState = useCallback(
    (newState: { freshchatInitialized?: boolean | null; berryInitialized?: boolean | null }) => {
      setInitializedState(state => ({ ...state, ...newState }));
    },
    []
  );

  const { closeChatbox, toggleFreshchat, updateFreshchatProperties } = useFreshchatHelpers();
  const {
    showBerry,
    toggleBerrychat,
    sendFeedbackFromBerry,
    isBerryInitialized,
    updateBerryHandoffData,
  } = useBerryHelpers();

  const updateFreshchatShowFirst = useCallback(show => {
    const fcWidget = (window as any)?.fcWidget;
    if (fcWidget) {
      fcWidget.showFreshchatFirst = !!show;
    }
  }, []);

  const shouldFreshchatShowFirst = useCallback(() => {
    const freshchatData = getFreshchatData() || {};
    if (
      (freshchatData?.conversationAlias && !shouldFreshChatClosed(freshchatData?.createdMillis)) ||
      freshchatData?.csatId
    ) {
      updateFreshchatShowFirst(true);
      return true;
    }

    return false;
  }, []);

  useEffect(() => {
    const { freshchatInitialized, berryInitialized } = initializedState;

    if (!isBerryChatDisabled) {
      if (shouldFreshchatShowFirst?.() && freshchatInitialized) {
        toggleBerrychat({ hideButton: true, hideChatbox: true });
        toggleFreshchat({ hideButton: false, hideChatbox: true });
      } else if (berryInitialized && freshchatInitialized) {
        toggleBerrychat({ hideButton: false, hideChatbox: true });
        toggleFreshchat({ hideButton: true, hideChatbox: true });
      } else if (berryInitialized) {
        toggleBerrychat({ hideButton: true, hideChatbox: true });
      } else if (!isNull(berryInitialized) && freshchatInitialized) {
        toggleFreshchat({ hideButton: false, hideChatbox: true });
      }
    } else if (freshchatInitialized) {
      toggleFreshchat({ hideButton: false, hideChatbox: true });
    }
  }, [
    initializedState,
    isBerryChatDisabled,
    shouldFreshchatShowFirst,
    toggleBerrychat,
    toggleFreshchat,
  ]);

  const onUserCreation = useCallback(
    response => {
      const status = response.status;
      const data = response?.data;
      if (status === 200) {
        const { restoreId } = data;
        if (restoreId && companyId && userId) {
          updateFreshchatRestoreId(companyId, userId, restoreId);
        }
      }
    },
    [companyId, userId]
  );

  const onMessageSent = useCallback(data => {
    const conversationAlias = data.message.conversationAlias;
    const createdMillis = data.message.createdMillis;
    if (conversationAlias) {
      updateFreshchatData({ conversationAlias, createdMillis });
    }
  }, []);

  const onMessageReceived = useCallback(
    data => {
      const conversationAlias = data.message.conversationAlias;
      const createdMillis = data.message.createdMillis;
      if (conversationAlias) {
        updateFreshchatData({ conversationAlias, createdMillis });
        toggleBerrychat({ hideButton: true, hideChatbox: true });
      }
    },
    [toggleBerrychat]
  );

  const onResolve = useCallback(data => {
    updateFreshchatShowFirst(false);
    updateFreshchatData({});
  }, []);

  const onWidgetClosed = () => {
    if (!isBerryInitialized()) {
      toggleFreshchat({ hideButton: false, hideChatbox: true });
      return;
    }

    const freshchatData = getFreshchatData() || {};
    if (
      (freshchatData?.conversationAlias && !shouldFreshChatClosed(freshchatData?.createdMillis)) ||
      freshchatData?.csatId
    ) {
      toggleBerrychat({ hideButton: true, hideChatbox: true });
      toggleFreshchat({ hideButton: false, hideChatbox: true });
      return;
    } else {
      toggleBerrychat({ hideButton: false, hideChatbox: true });
      toggleFreshchat({ hideButton: true, hideChatbox: true });
    }
  };

  const onWidgetLoaded = () => {
    updateInitializedState({ freshchatInitialized: true });
  };

  const onCsatReceived = (resp: any) => {
    const csatId = resp?.data?.csatId ? true : false;
    if (resp?.status === 200 && csatId) {
      updateFreshchatData({ csatId });
      updateFreshchatShowFirst(csatId);
    }

    if (!isBerryInitialized()) {
      toggleFreshchat({ hideButton: false, hideChatbox: true });
    } else {
      toggleBerrychat({ hideButton: csatId, hideChatbox: csatId });
      toggleFreshchat({ hideButton: !csatId, hideChatbox: csatId });
    }
  };

  const onCsatUpdated = (resp: any) => {
    const isResolved = resp?.data?.message?.csatResponse?.issueResolved || false;
    if (isResolved) {
      updateFreshchatData({});
      updateFreshchatShowFirst(false);
      closeChatbox();
      showBerry();
    } else {
      toggleFreshchat({ hideButton: false, hideChatbox: false });
      toggleBerrychat({ hideButton: true, hideChatbox: true });
    }
  };

  const onBerryHandOff = (data: {
    berryAdminChatUrl?: string | undefined;
    learningSite?: string | undefined;
  }) => {
    updateFreshchatProperties({ ...data, learningSite: auth?.company.learningSite });
    toggleFreshchat({ hideButton: false, hideChatbox: false });
  };

  const onAdminBerryHandOff = (data: any = {}) => {
    toggleBerrychat({ hideButton: true, hideChatbox: true });
    onBerryHandOff?.(data);
  };

  const onLearnerBerryHandOff = async (data: any = {}) => {
    toggleBerrychat({ hideButton: true, hideChatbox: true });
    updateBerryHandoffData(data);
    sendFeedbackFromBerry(data);
  };

  const onBerryHandShake = () => {
    updateInitializedState({ berryInitialized: true });
    toggleBerrychat({ hideButton: true, hideChatbox: true });
  };

  const eventHandlers = !isBerryChatDisabled
    ? {
        onUserCreation,
        onMessageSent,
        onMessageReceived,
        onResolve,
        onWidgetClosed,
        onWidgetLoaded,
        onCsatReceived,
        onCsatUpdated,
        onBerryHandOff,
      }
    : { onUserCreation };

  const berryEventHandlers = !isBerryChatDisabled
    ? { onAdminBerryHandOff, onLearnerBerryHandOff, onBerryHandShake }
    : {};

  return {
    eventHandlers,
    berryEventHandlers,
  };
};

export default useFreshchatHandlers;
